<template>
  <base-modal
    :width="420"
    :title="'Change your name'"
    @close-modal="closeModal"
  >
    <form class="wrapper" @submit="onSubmit">
      <div class="field">
        <div class="control">
          <input
            v-model="firstName"
            name="firstName"
            class="text-input"
            spellcheck="false"
            placeholder="First name"
          />
          <span :class="['help is-danger']">{{ errors.firstName }}</span>
        </div>
      </div>
      <div class="field">
        <div class="control">
          <input
            v-model="lastName"
            name="lastName"
            class="text-input"
            spellcheck="false"
            placeholder="Last name"
          />
          <span :class="['help is-danger']">{{ errors.lastName }}</span>
        </div>
      </div>
      <div :class="['help is-danger']">{{ serverError }}</div>
      <div class="buttons">
        <button
          class="button confirm"
          :class="{ 'is-loading': isLoading }"
          type="submit"
          :disabled="isLoading"
        >
          <span>Change name</span>
        </button>
      </div>
    </form>
  </base-modal>
</template>

<script>
import { ref, computed, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import { useField, useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import { z } from 'zod';
import BaseModal from '@components/modals/BaseModal.vue';

const validationSchema = toTypedSchema(
  z.object({
    firstName: z
      .string()
      .min(1, {
        message: 'The first name field is required',
      })
      .max(255)
      .regex(/^[\p{L}\p{M}\p{N}\s-]+$/u, {
        message: 'The first name field must be a valid name.',
      }),
    lastName: z
      .string()
      .min(1, {
        message: 'The last name field is required',
      })
      .max(255)
      .regex(/^[\p{L}\p{M}\p{N}\s-]+$/u, {
        message: 'The last name field must be a valid name.',
      }),
  })
);

export default {
  components: {
    BaseModal,
  },

  setup() {
    const store = useStore();
    const isLoading = ref(false);
    const instance = getCurrentInstance();
    const serverError = ref(null);

    const { handleSubmit, errors } = useForm({
      validationSchema,
    });

    const { value: firstName } = useField('firstName');
    const { value: lastName } = useField('lastName');

    const getFirstName = computed(
      () => store.getters['user/account/getFirstName']
    );
    const getLastName = computed(
      () => store.getters['user/account/getLastName']
    );

    firstName.value = getFirstName.value;
    lastName.value = getLastName.value;

    const closeModal = () => {
      instance.emit('closeModal');
    };

    const onSubmit = handleSubmit(values => {
      isLoading.value = true;
      store
        .dispatch('user/account/updateName', {
          firstName: firstName.value,
          lastName: lastName.value,
        })
        .then(() => {
          closeModal();
        })
        .catch(error => {
          serverError.value = 'Something went wrong. Please try again.';
        })
        .finally(() => {
          isLoading.value = false;
        });
    });

    return {
      onSubmit,
      errors,
      firstName,
      lastName,
      isLoading,
      closeModal,
      serverError,
    };
  },
};
</script>

<style scoped lang="scss">
.wrapper {
  padding: 20px;
}

.field {
  + .field {
    margin-top: 20px;
  }
}

input {
  padding: 10px;
  background: #252929;
  border: none;
  color: #bcbcbc;
  width: 100%;
  overflow: hidden;
  font-family: 'Open sans';
  font-size: 15px;
}

a {
  color: #db841e;
  text-decoration: none;
}

h1 {
  font-weight: 100;
  font-size: 20px;
  line-height: 1.5;
  padding: 0;
  margin: 0 0 20px 0;
}

.icon {
  display: block;
  text-align: center;
  margin: 40px auto 30px auto;
}

p {
  font-size: 15px;
  line-height: 1.5;
}

.buttons {
  display: flex;
  justify-content: flex-end;
  .button {
    font-size: 15px;
    padding: 10px 18px;
    border-radius: 3px;
    width: 100%;
    border: none;
    color: #fff;
    background: #bf751d;
    cursor: pointer;
    margin: 25px 0 0 0;
    display: inline-block;
    width: auto;
    text-align: center;

    &.is-loading {
      &:before {
        margin: -12px 0 0 -11px;
        width: 22px;
        height: 22px;
        position: absolute;
        left: 50%;
        top: 50%;
        content: '';
        -webkit-border-radius: 24px;
        -webkit-background-clip: padding-box;
        -moz-border-radius: 24px;
        -moz-background-clip: padding;
        border-radius: 24px;
        background-clip: padding-box;
        border: rgba(255, 255, 255, 0.25) 2px solid;
        border-top-color: #fff;
        animation: animation-rotate 2000ms linear infinite;
        // -webkit-animation: animation-rotate 2000ms linear infinite;
        // -moz-animation: animation-rotate 2000ms linear infinite;
        // -o-animation: animation-rotate 2000ms linear infinite;
      }

      span {
        color: transparent;
      }
    }
  }
}

.help {
  display: block;
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

.help.is-danger {
  color: #bf751d;
}

@-webkit-keyframes animation-rotate {
  100% {
    -webkit-transform: rotate(360deg);
  }
}
@-moz-keyframes animation-rotate {
  100% {
    -moz-transform: rotate(360deg);
  }
}
@-o-keyframes animation-rotate {
  100% {
    -o-transform: rotate(360deg);
  }
}
@keyframes animation-rotate {
  100% {
    transform: rotate(360deg);
  }
}
</style>
